import DOMPurify from "dompurify"

/*
  Comment and note bodies are stored as HTML and rendered as HTML. They are
  written by users, through the TipTap composer in `rich-text-editor.tsx` and,
  for older rows, through the `document.execCommand` editor that preceded it.
  Anyone who can post a comment can therefore store markup that runs for every
  teammate who later opens that record — a stored-XSS path, not a theoretical
  one, because the body travels to the API and back untouched.

  This module is the one place that decides what stored markup may become DOM.
  It is deliberately a thin wrapper over DOMPurify rather than a hand-written
  tag filter: sanitizing HTML correctly means surviving mutation XSS, where
  markup that looks inert re-parses into something live after the browser
  fixes it up, and a bespoke allow-list walker does not survive that. The value
  this file adds is the policy, not the parsing.
*/

/**
 * Everything the composer can produce, plus what the old editor left behind.
 *
 * The composer is TipTap's StarterKit with headings at levels 2 and 3, so the
 * live set is paragraphs, bold, italic, strike, inline code, code blocks,
 * blockquotes, both lists, horizontal rules, hard breaks and links. The rest —
 * `div`, `u`, `strike`, `h1`, `h4`–`h6`, `span` — is not reachable from the
 * toolbar but exists in bodies written before it, and dropping those tags
 * would silently reformat history.
 */
const ALLOWED_TAGS = [
  "p", "br", "hr", "div", "span",
  "strong", "b", "em", "i", "u", "s", "strike", "code", "pre",
  "blockquote",
  "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "a",
]

/**
 * No `style`, and no `class`.
 *
 * `style` is an injection surface of its own and would also let a note repaint
 * itself against the app's own theme. `class` is dropped because the surfaces
 * that render this supply their own typography — a stored `class` could only
 * fight it, and Tailwind's classes are not a vocabulary user content should be
 * writing in.
 */
const ALLOWED_ATTR = ["href", "title", "lang", "dir", "start", "type", "colspan", "rowspan"]

/*
  DOMPurify already refuses `javascript:` and `data:` URLs on `href`. This adds
  the part it cannot know: a link in a comment points somewhere outside the
  app, so it opens in its own tab and is denied access to the opener.
*/
let hookInstalled = false
function installLinkHook() {
  if (hookInstalled) return
  hookInstalled = true
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName !== "A") return
    if (!node.getAttribute("href")) return
    node.setAttribute("target", "_blank")
    node.setAttribute("rel", "noopener noreferrer nofollow")
  })
}

/**
 * Stored rich text, reduced to markup that is safe to mount.
 *
 * Returns a string rather than nodes because every call site renders through
 * `RichText`, which is the only component allowed to hand HTML to React. An
 * empty or absent body sanitizes to `""` so callers can test it for emptiness
 * without a second null check.
 */
export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return ""
  installLinkHook()
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // `<template>`, `<math>` and `<svg>` are the usual mutation-XSS carriers,
    // and none of them is markup a comment has any reason to contain.
    FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form", "input", "svg", "math", "template"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    /*
      No `USE_PROFILES`. It reads like a tightening and is the opposite: the
      profile's own tag and attribute sets are merged with the ones above
      rather than intersected with them, so asking for the html profile put
      `class` and `style` back after this file had just excluded them. The
      lists above are the whole policy.
    */
  })
}
